import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { settingsService } from '../../services/api';
import { styled } from '@mui/material/styles';
import { useToast } from '../../context/ToastContext';
import { useDropzone } from 'react-dropzone';
import { Upload as UploadIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));
const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px'
});

function EmailConfiguration() {
  const [settings, setSettings] = useState(null);
  //const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(false);
  const [editingSignature, setEditingSignature] = useState(false);
  const [editingSalutation, setEditingSalutation] = useState(false);
  const [bannerType, setBannerType] = useState('1'); // 1 for color, 2 for image
  const fileInputRef = useRef(null);
  const { showToast } = useToast();
  const [selectedImage, setSelectedImage] = useState();
  const [previewUrl, setPreviewUrl] = useState();
  const [image, setImage] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      //setLoading(true);
      const response = await settingsService.getEmailConfiguration();
      setSettings(response.data.settingForm);
      setBannerType(response.data.settingForm.field || '1');
    } catch (error) {
      console.error('Error fetching email configuration:', error);
      showToast('Failed to load email configuration', 'error');
    }
    // finally {
    //   setLoading(false);
    // }
  }, [showToast]);


  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleBannerTypeChange = (event) => {
    setBannerType(event.target.value);
  };
  // const { getRootProps, getInputProps, isDragActive } = useDropzone({
  //   onDrop: (acceptedFiles) => {
  //     setImage(prev => [...prev, ...acceptedFiles]);
  //   },
  //   accept: '.jpg,.jpeg,.png'
  // });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    // Just update the file input, actual upload happens on save
  };

  const handleSaveBanner = async () => {
    try {
      const formData = new FormData();
      formData.append('id', settings.id);
      formData.append('content', settings.title);
      formData.append('field', bannerType);

      if (bannerType === '1') {
        formData.append('color', settings.color);
      } else if (bannerType === '2' && selectedImage) {
        formData.append('image', selectedImage);
      }
      // console.log("Selected image:", selectedImage);


      await settingsService.updateEmailConfiguration(formData);
      showToast('Banner updated successfully', 'success');
      setEditingBanner(false);
      setSelectedImage(null);
      setPreviewUrl(null);
      fetchSettings(); // Refresh settings
    } catch (error) {
      console.error('Error updating banner:', error);
      showToast('Failed to update banner', 'error');
    }
  };
  const handleFileDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setSettings(prev => ({ ...prev, image: URL.createObjectURL(file) }));

    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop, // ← connect your working logic
    accept: { 'image/*': ['.jpg', '.jpeg', '.png'] }
  });


  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setSettings(prev => ({ ...prev, image: null }));
  };


  const handleSaveSignature = async () => {
    try {
      const formData = new FormData();
      formData.append('id', settings.id);
      formData.append('signature', settings.signature);

      await settingsService.updateEmailConfiguration(formData);
      showToast('Signature updated successfully', 'success');
      setEditingSignature(false);
    } catch (error) {
      console.error('Error updating signature:', error);
      showToast('Failed to update signature', 'error');
    }
  };

  const handleSaveSalutation = async () => {
    try {
      const formData = new FormData();
      formData.append('id', settings.id);
      formData.append('salutation', settings.salutation);

      await settingsService.updateEmailConfiguration(formData);
      showToast('Salutation updated successfully', 'success');
      setEditingSalutation(false);
    } catch (error) {
      console.error('Error updating salutation:', error);
      showToast('Failed to update salutation', 'error');
    }
  };
  if (!settings) {
    return null;
  }
  // if (loading || !settings) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Configuration
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Sender Information
              </Typography>
              <Typography>
                <strong>{settings.sender_name}</strong>, {settings.sender_address}
              </Typography>
            </Box>
          </Grid>

          {/* Email Banner Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Email Banner
              </Typography>
              {!editingBanner ? (
                <Button color="primary" onClick={() => setEditingBanner(true)}>
                  Edit
                </Button>
              ) : (
                <Button color="success" onClick={handleSaveBanner}>
                  Save
                </Button>
              )}
            </Box>

            {editingBanner ? (
              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Banner Type</FormLabel>
                  <RadioGroup
                    row
                    name="field"
                    value={bannerType}
                    onChange={handleBannerTypeChange}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="Choose Color" />
                    <FormControlLabel value="2" control={<Radio />} label="Upload Image" />
                  </RadioGroup>
                </FormControl>

                {bannerType === '1' && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={settings.color || '#2D7DC2'}
                      onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                      style={{ width: '50px', height: '30px' }}
                    />
                    <TextField
                      size="small"
                      value={settings.color || '#2D7DC2'}
                      onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="#2D7DC2"
                    />
                  </Box>
                )}

                {bannerType === '2' && (
                  <DropzoneArea {...getRootProps()}>
                    <input {...getInputProps()} />

                    {(previewUrl || settings.image) ? (
                      <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                        <PreviewImage src={previewUrl || settings.image} alt="Banner" />
                        <IconButton
                          onClick={handleRemoveImage}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <>
                        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography>
                          {isDragActive ? 'Drop files here...' : 'Drag & drop or click to upload'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Supported formats:  Images [.jpeg, .jpg, .png]
                        </Typography>
                      </>
                    )}
                  </DropzoneArea>
                )}
                <TextField
                  fullWidth
                  label="Banner Text"
                  name="title"
                  value={settings.title || ''}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px',
                  p: 2,
                  textAlign: 'center',
                  color: 'white',
                  ...(String(settings.field) === '1'
                    ? { backgroundColor: settings.color || '#2D7DC2' }
                    : {
                      backgroundImage: `url(${settings.image}?v=${Date.now()})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                    }),

                }}
              >
                {settings.title}
              </Box>

            )}
          </Grid>


          {/* Email Signature Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Email Signature
              </Typography>
              {!editingSignature ? (
                <Button color="primary" onClick={() => setEditingSignature(true)}>
                  Edit
                </Button>
              ) : (
                <Button color="success" onClick={handleSaveSignature}>
                  Save
                </Button>
              )}
            </Box>

            {editingSignature ? (
              <TextField
                fullWidth
                multiline
                rows={3}
                name="signature"
                value={settings.signature || ''}
                onChange={handleInputChange}
                variant="outlined"
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px',
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  color: '#333'
                }}
              >
                {settings.signature}
              </Box>
            )}
          </Grid>

          {/* Email Salutation Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Email Salutation
              </Typography>
              {!editingSalutation ? (
                <Button color="primary" onClick={() => setEditingSalutation(true)}>
                  Edit
                </Button>
              ) : (
                <Button color="success" onClick={handleSaveSalutation}>
                  Save
                </Button>
              )}
            </Box>

            {editingSalutation ? (
              <TextField
                fullWidth
                multiline
                rows={2}
                name="salutation"
                value={settings.salutation || ''}
                onChange={handleInputChange}
                variant="outlined"
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px',
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  color: '#333'
                }}
              >
                {settings.salutation}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default EmailConfiguration;